import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ConfigStateService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { OAuthService } from 'angular-oauth2-oidc';
import { PublicCoachRegistrationService } from '../../proxy/http-api/controllers/public/public-coach-registration.service';
import { from, EMPTY, Subject } from 'rxjs';
import { finalize, switchMap, catchError, tap, debounceTime, takeUntil, distinctUntilChanged } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'app-custom-register',
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class CustomRegisterComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private oAuthService = inject(OAuthService);
  private configState = inject(ConfigStateService);
  private toasterService = inject(ToasterService);
  private coachRegistrationService = inject(PublicCoachRegistrationService);
  private destroy$ = new Subject<void>();

  registerForm!: FormGroup;
  showPassword = false;
  showConfirmPassword = false;
  isLoading = false;
  isCheckingUsername = false;
  usernameAvailable: boolean | null = null;
  passwordStrength = { level: '', text: '' };

  private static readonly USERNAME_PATTERN = /^[a-zA-Z0-9._-]+$/;

  ngOnInit(): void {
    this.registerForm = this.fb.group(
      {
        userName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern(CustomRegisterComponent.USERNAME_PATTERN)]],
        name: ['', [Validators.required, Validators.minLength(2)]],
        surname: ['', [Validators.required, Validators.minLength(2)]],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(6), Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/)]],
        confirmPassword: ['', [Validators.required]],
        termsAccepted: [false, [Validators.requiredTrue]],
      },
      { validators: this.passwordMatchValidator },
    );

    this.registerForm.get('password')?.valueChanges.subscribe(value => {
      this.updatePasswordStrength(value);
    });

    // Email değiştiğinde otomatik username öner
    this.registerForm.get('email')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
    ).subscribe(email => {
      this.suggestUsername(email);
    });

    // Username değiştiğinde availability'yi sıfırla
    this.registerForm.get('userName')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
    ).subscribe(() => {
      this.usernameAvailable = null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  suggestUsername(email: string): void {
    if (!email || !email.includes('@')) return;

    const userNameControl = this.registerForm.get('userName');
    // Sadece userName boşken veya daha önce otomatik set edilmişken öner
    if (userNameControl?.value && userNameControl.dirty) return;

    const localPart = email.split('@')[0];
    const sanitized = localPart.replace(/[^a-zA-Z0-9._-]/g, '').toLowerCase();
    if (sanitized.length >= 3) {
      userNameControl?.setValue(sanitized);
      userNameControl?.markAsPristine();
      this.checkUsernameAvailability();
    }
  }

  checkUsernameAvailability(): void {
    const userNameControl = this.registerForm.get('userName');
    const userName = userNameControl?.value;

    if (!userName || userName.length < 3 || userNameControl?.hasError('pattern') || userNameControl?.hasError('minlength')) {
      this.usernameAvailable = null;
      return;
    }

    this.isCheckingUsername = true;
    this.coachRegistrationService.isUsernameAvailable(userName).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (isAvailable) => {
        this.isCheckingUsername = false;
        this.usernameAvailable = isAvailable;
        if (!isAvailable) {
          userNameControl?.setErrors({ ...userNameControl.errors, usernameTaken: true });
        } else {
          // Sadece usernameTaken hatasını kaldır, diğerlerini koru
          if (userNameControl?.hasError('usernameTaken')) {
            const errors = { ...userNameControl.errors };
            delete errors['usernameTaken'];
            userNameControl.setErrors(Object.keys(errors).length ? errors : null);
          }
        }
      },
      error: () => {
        this.isCheckingUsername = false;
        this.usernameAvailable = null;
      },
    });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  updatePasswordStrength(password: string): void {
    if (!password) {
      this.passwordStrength = { level: '', text: '' };
      return;
    }

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) {
      this.passwordStrength = { level: 'weak', text: 'Zayıf şifre' };
    } else if (strength <= 3) {
      this.passwordStrength = { level: 'medium', text: 'Orta güçlükte' };
    } else {
      this.passwordStrength = { level: 'strong', text: 'Güçlü şifre' };
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.registerForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  onSubmit(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const { userName, name, surname, email, password } = this.registerForm.value;

    this.coachRegistrationService
      .simpleRegister({
        userName,
        email,
        name,
        surname,
        password,
      })
      .pipe(
        tap(() => {
          this.toasterService.success('Hesabınız başarıyla oluşturuldu! Giriş yapılıyor...', 'Kayıt Başarılı');
        }),
        switchMap(() => {
          return from(this.oAuthService.fetchTokenUsingPasswordFlow(userName, password));
        }),
        switchMap(() => this.configState.refreshAppState()),
        catchError((err: any) => {
          this.isLoading = false;

          // OAuth error - registration succeeded but auto-login failed
          if (err?.error?.error_description || err?.error?.error === 'invalid_grant') {
            this.toasterService.success('Hesabınız oluşturuldu! Lütfen giriş yapın.', 'Kayıt Başarılı');
            this.router.navigate(['/account/login']);
          }
          // ABP/HTTP errors are handled by global ToastErrorHandlerService
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => {
          this.router.navigateByUrl('/dashboard');
        },
      });
  }
}
